import React, { useEffect, useMemo, useState } from "react";
import { Box, Dialog, Grid, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";

import style from "./index.module.css";

import Modal from "@/components/Modal";
import PharmacyCard from "@/components/Medicines/PharmacyCard";
import Button from "@/components/Button";
import GetImages from "@/components/GetImages";
import AddDeliveryAddress from "@/components/Medicines/AddDeliveryAddress";
import GenericSuccessAlert from "@/components/GenericSuccessAlert";
import {
  AppointmentTab,
  AppointmentTabPanel,
  AppointmentTabsPanel,
} from "@/components/AppointmentTab";

import {
  medicine,
  resetCompleteMedicinePlacedOrder,
  setDeliveryAddressOpenClosed,
} from "@/store/slices/patient/medicine";
import { useAppDispatch, useAppSelector } from "@/store/hooks";

import { IMedicineOrderType } from "@/models/ApiRequestResponse/patient";
import { IMedicineModuleEnums } from "@/models/patient";

import { medicineOrderPlacedApi } from "@/utils/apis/patient/medicine";
import { INotifyEnum, notify } from "@/utils/toaster";
import useIsMobile from "@/utils/hooks/useIsMobile";
import useIsTablet from "@/utils/hooks/useIsTablet";
import { IDELIVERY_TYPE } from "@/utils";

interface IProps {
  isOpen: boolean;
  closeHandler: () => void;
}
const PlaceOrder: React.FC<IProps> = (props) => {
  const { isOpen, closeHandler } = props;
  const [showOrderPlacedSuccess, setShowOrderPlacedSuccess] =
    useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [showBothTabs, setShowBothTabs] = useState<boolean>(false);

  const {
    selectedPharmacy,
    isDeliveryAddressOpen,
    medicineOrderPlaced,
    medicineDeliveryType,
  } = useAppSelector(medicine);
  const dispatch = useAppDispatch();

  const navigate = useNavigate();
  const { isMobile } = useIsMobile();
  const { isTablet } = useIsTablet();
  const intl = useIntl();

  const openCloseOrderPlacedSuccessHandler = () => {
    setShowOrderPlacedSuccess(!showOrderPlacedSuccess);
  };

  const closeSuccessHandler = () => {
    openCloseOrderPlacedSuccessHandler();
    navigate("/home", { state: { navItem: 0 } });
    dispatch(resetCompleteMedicinePlacedOrder());
  };

  const placeOrderHandler = async () => {
    try {
      const medicineOrderPlacedResponse = await medicineOrderPlacedApi({
        medicalRecordId: medicineOrderPlaced?.medicalRecordId,
        memberId: medicineOrderPlaced?.memberId,
        pharmacyId: medicineOrderPlaced?.pharmacyId,
        type:
          selectedTab === 0
            ? IMedicineOrderType.HOME_DELIVERY
            : IMedicineOrderType.PICKUP,
      });
      if ("result" in medicineOrderPlacedResponse) {
        console.log("MedicineOrder", medicineOrderPlacedResponse);
        notify(INotifyEnum.SUCCESS, medicineOrderPlacedResponse?.message);
        openCloseOrderPlacedSuccessHandler();
      } else {
        notify(INotifyEnum.ERROR, medicineOrderPlacedResponse?.message);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const handleMedicineOrderTab = (
    event: React.SyntheticEvent,
    newValue: number
  ) => {
    setSelectedTab(newValue);
  };

  const isSmallScreen = useMemo(() => {
    return isMobile || isTablet;
  }, [isMobile, isTablet]);

  useEffect(() => {
    if (isSmallScreen) {
      const bothEnabled =
        selectedPharmacy.deliveryEnabled && selectedPharmacy.pickupEnabled;
      setSelectedTab(selectedPharmacy.deliveryEnabled ? 0 : 1);
      setShowBothTabs(bothEnabled);
    } else {
      setSelectedTab(
        medicineDeliveryType === IDELIVERY_TYPE.HOME_DELIVERY ? 0 : 1
      );
      setShowBothTabs(false);
    }
  }, [isOpen]);

  if (isSmallScreen) {
    return (
      <>
        <Modal isOpen={isOpen} closeHandler={closeHandler}>
          {showBothTabs && (
            <Grid item>
              <AppointmentTabsPanel
                value={selectedTab}
                onChange={handleMedicineOrderTab}
                aria-label="basic tabs example"
                className={style.tabBtnContainer}
              >
                <AppointmentTabPanel
                  label={
                    <Grid item className={style.tabInnerContainer}>
                      <GetImages
                        name={
                          selectedTab
                            ? "LocationAccessSmallIcon"
                            : "LocationRedirectIcon"
                        }
                        width="20"
                        height="20"
                      />
                      <Typography component={"p"}>Home Delivery</Typography>
                    </Grid>
                  }
                />
                <AppointmentTabPanel
                  label={
                    <Grid item className={style.tabInnerContainer}>
                      <GetImages
                        name={
                          selectedTab
                            ? "MedicineWhiteIcon"
                            : "MedicineSmallIcon"
                        }
                        width="20"
                        height="20"
                      />
                      <Typography component={"p"}>Pick up</Typography>
                    </Grid>
                  }
                />
              </AppointmentTabsPanel>
            </Grid>
          )}
          <PharmacyCard
            isBackgroundColor={false}
            hasMap
            {...selectedPharmacy}
          />
          <AppointmentTab value={selectedTab} index={0}>
            <AddDeliveryAddress showDeliveryAddress />
          </AppointmentTab>
          <Grid item className={style.btnContainer}>
            <Button btnTrigger={placeOrderHandler}>
              <GetImages name="PlacedTickIcon" width="28" height="20" />
              {intl.formatMessage({
                id: "placeOrder",
                defaultMessage: "Place Order",
              })}
            </Button>
          </Grid>

          {isDeliveryAddressOpen && (
            <AddDeliveryAddress
              isOpen={isDeliveryAddressOpen}
              closeHandler={() => dispatch(setDeliveryAddressOpenClosed())}
            />
          )}
        </Modal>
        {showOrderPlacedSuccess && (
          <GenericSuccessAlert
            closeScheduleModal={closeSuccessHandler}
            type={IMedicineModuleEnums.MEDICINE}
            isOpen={true}
            date="Delivery ID Will be Update Soon"
            bio={`${selectedPharmacy?.address}, ${selectedPharmacy?.city}`}
            name={selectedPharmacy?.name}
            btnLabel="go_to_home"
          />
        )}
      </>
    );
  }

  return (
    <>
      <Dialog open={isOpen} onClose={closeHandler} maxWidth="sm" fullWidth>
        <Box p={2}>
          {showBothTabs && (
            <Stack justifyContent={"center"} alignItems={"center"} mx={"auto"}>
              <AppointmentTabsPanel
                value={selectedTab}
                onChange={handleMedicineOrderTab}
                aria-label="basic tabs example"
                className={style.tabBtnContainer}
                sx={{
                  width: "100%",
                }}
              >
                <AppointmentTabPanel
                  label={
                    <Box width={"100%"} className={style.tabInnerContainer}>
                      <GetImages
                        name={
                          selectedTab
                            ? "LocationAccessSmallIcon"
                            : "LocationRedirectIcon"
                        }
                        width="20"
                        height="20"
                      />
                      <Typography component={"p"}>
                        <FormattedMessage
                          defaultMessage={"Home Delivery"}
                          id="home_delivery"
                        />
                      </Typography>
                    </Box>
                  }
                />
                <AppointmentTabPanel
                  label={
                    <Box width={"100%"} className={style.tabInnerContainer}>
                      <GetImages
                        name={
                          selectedTab
                            ? "MedicineWhiteIcon"
                            : "MedicineSmallIcon"
                        }
                        width="20"
                        height="20"
                      />
                      <FormattedMessage
                        defaultMessage={"Pick Up"}
                        id="pick_up"
                      />
                    </Box>
                  }
                />
              </AppointmentTabsPanel>
            </Stack>
          )}
          {(isSmallScreen ||
            medicineDeliveryType === IDELIVERY_TYPE.PICKUP) && (
            <PharmacyCard
              isBackgroundColor={false}
              hasMap
              {...selectedPharmacy}
            />
          )}
          <AppointmentTab value={selectedTab} index={0}>
            <AddDeliveryAddress showDeliveryAddress />
          </AppointmentTab>
          <Grid item className={style.btnContainer}>
            <Button btnTrigger={placeOrderHandler}>
              {intl.formatMessage({
                id: "saveConfirm",
                defaultMessage: "Save & Confirm",
              })}
            </Button>
          </Grid>

          {isDeliveryAddressOpen && (
            <AddDeliveryAddress
              isOpen={isDeliveryAddressOpen}
              closeHandler={() => dispatch(setDeliveryAddressOpenClosed())}
            />
          )}
        </Box>
      </Dialog>
      {showOrderPlacedSuccess && (
        <GenericSuccessAlert
          closeScheduleModal={closeSuccessHandler}
          type={IMedicineModuleEnums.MEDICINE}
          deliveryType={
            selectedTab === 0
              ? IMedicineOrderType.HOME_DELIVERY
              : IMedicineOrderType.PICKUP
          }
          isOpen={true}
          date="Delivery ID Will be Update Soon"
          bio={`${selectedPharmacy?.address}, ${selectedPharmacy?.city}`}
          name={selectedPharmacy?.name}
          btnLabel="go_to_home"
          latitude={selectedPharmacy?.latitude}
          longitude={selectedPharmacy?.longitude}
        />
      )}
    </>
  );
};

export default PlaceOrder;
